import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const scalingLawsComponent: ComponentDef = {
  id: 'scaling-laws',
  name: 'Scaling Laws',
  description: 'Relate model size, data, and compute to final loss using power-law scaling.',
  category: 'training',
  params: [
    { name: 'params', label: 'Parameters (millions)', type: 'number', default: 1000, min: 1, max: 1000000, step: 1 },
    { name: 'tokens', label: 'Tokens (billions)', type: 'number', default: 20, min: 1, max: 10000, step: 1 },
    {
      name: 'variant',
      label: 'Analysis',
      type: 'select',
      default: 'chinchilla',
      options: [
        { label: 'Chinchilla Optimal', value: 'chinchilla' },
        { label: 'FLOPs Estimation', value: 'flops' },
        { label: 'Loss Prediction', value: 'loss_prediction' }
      ]
    }
  ],
  buildGraph: (params) => {
    const p = Number(params.params);
    const t = Number(params.tokens);
    const variant = String(params.variant || 'chinchilla');

    const g = new GraphBuilder();

    if (variant === 'chinchilla') {
      // Chinchilla Optimal: N_opt ∝ C^a, D_opt ∝ C^b with a ≈ b ≈ 0.5
      g.input('compute_budget', [p, t]);

      // Optimal allocation: parameters and tokens should scale equally
      g.scale('n_opt', 'compute_budget', 'N_opt = a · C^0.5');
      g.scale('d_opt', 'compute_budget', 'D_opt = b · C^0.5');

      // Chinchilla loss: L(N,D) = E + A/N^α + B/D^β
      g.scale('param_term', 'n_opt', 'A / N^α');
      g.scale('data_term', 'd_opt', 'B / D^β');
      g.add('loss', 'param_term', 'data_term');
      g.scale('chinchilla_loss', 'loss', 'L_chinchilla = E + A/N^α + B/D^β');
      g.output('out', 'chinchilla_loss');

    } else if (variant === 'flops') {
      // FLOPs Estimation: C ≈ 6ND for transformer training
      g.input('model_config', [p, t]);

      // FLOPs breakdown
      g.scale('forward_flops', 'model_config', '2ND (forward pass)');
      g.scale('backward_flops', 'model_config', '4ND (backward pass)');
      g.add('total_flops', 'forward_flops', 'backward_flops');

      // Hardware utilization
      g.scale('gpu_hours', 'total_flops', 'FLOPs / (GPU FLOPS × hours)');
      g.scale('mfu', 'gpu_hours', 'Model FLOPs Utilization (MFU)');
      g.metric('compute', 'mfu', 'total_flops');
      g.output('out', 'compute');

    } else {
      // Loss Prediction: predict final loss from N, D, C using power-law fits
      g.input('scaling_inputs', [p, t]);

      // Kaplan et al. power law: L(N) = (N_c / N)^α_N
      g.scale('kaplan_n', 'scaling_inputs', '(N_c / N)^α_N');
      // Kaplan data law: L(D) = (D_c / D)^α_D
      g.scale('kaplan_d', 'scaling_inputs', '(D_c / D)^α_D');
      // Kaplan compute law: L(C) = (C_c / C)^α_C
      g.scale('kaplan_c', 'scaling_inputs', '(C_c / C)^α_C');

      // Chinchilla correction (breaks Kaplan independence assumption)
      g.add('combined_prediction', 'kaplan_n', 'kaplan_d');
      g.add('full_prediction', 'combined_prediction', 'kaplan_c');
      g.scale('predicted_loss', 'full_prediction', 'Predicted L(N, D, C)');
      g.metric('loss_metric', 'predicted_loss', 'predicted_loss');
      g.output('out', 'loss_metric');
    }

    return g.build();
  }
};
